import React, { useMemo } from 'react';
import { useTheme } from '@mui/material';
import { SimpleProjectionYearResult } from '../../../../../types/SimpleProjection';
import { useStockCurrency } from '../../../../../contexts/StockCurrencyContext';
import { getChartColors } from '../../../Charts/constants/chartColors';
import ChartTooltip from '../../../Charts/components/shared/ChartTooltip';
import GenericFinancialBarChart from '../../../Charts/components/shared/GenericFinancialBarChart';

interface ProjectedEPSChartProps {
  projections: SimpleProjectionYearResult[];
  ticker: string;
  transparentBg?: boolean;
}

const formatEPS = (value: number): string => {
  const abs = Math.abs(value);
  const formatted = abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return formatted;
};

const ProjectedEPSChart: React.FC<ProjectedEPSChartProps> = ({ projections, ticker, transparentBg = false }) => {
  const theme = useTheme();
  const { currencySymbol } = useStockCurrency();
  const colors = getChartColors(theme.palette.mode === 'dark');

  const chartData = useMemo(() =>
    projections.map((p) => ({
      period: p.isHistorical ? `${p.yearLabel} (A)` : p.yearLabel,
      date: `${p.yearLabel}-01-01`,
      year: parseInt(p.yearLabel),
      quarter: null,
      eps: p.eps,
    })),
    [projections]
  );

  return (
    <GenericFinancialBarChart
      title="Projected EPS"
      ticker={ticker}
      height={350}
      renderCard={!transparentBg}
      isLoading={false}
      error={null}
      chartData={chartData}
      viewMode="annual"
      barKeys={['eps']}
      colors={(bar: { data: Record<string, number> }) =>
        (bar.data.eps ?? 0) >= 0 ? colors.positive : colors.negative
      }
      yAxisLegend={`EPS (${currencySymbol})`}
      yAxisFormat={(value) => `${value >= 0 ? currencySymbol : `-${currencySymbol}`}${formatEPS(value)}`}
      tooltipContent={({ indexValue, value, color }) => {
        const v = value as number;
        return (
          <ChartTooltip
            header={String(indexValue)}
            headerColor={color}
            rows={[{ label: 'EPS', value: `${v >= 0 ? currencySymbol : `-${currencySymbol}`}${formatEPS(v)}` }]}
          />
        );
      }}
    />
  );
};

export default ProjectedEPSChart;
